import { apiClient } from './client';

export type DeviceStatus = 'active' | 'suspended' | 'lost' | 'retired';

export interface Device {
  id: string;
  label: string;
  status: DeviceStatus;
  os_name: string | null;
  os_version: string | null;
  app_version: string | null;
  /**
   * Stable OS device id — the cross-tenant physical-device key. The same
   * tablet carries the same value in every tenant it's enrolled in, so
   * support can recognise it across tenants. Null on devices enrolled
   * before the identity feature (self-fills on the device's next check-in).
   */
  hardware_id: string | null;
  enrolled_at: string;
  last_seen_at: string | null;
  /**
   * Default org context stamped onto this device's recordings
   * (RFC procedure-instance, Step 1). Null = no default.
   */
  default_department_id: string | null;
  default_location_id: string | null;
  /** Edge appliance this device is paired with (its cameras record when the
   *  device starts a session). Null = not paired. */
  appliance_id: string | null;
}

export interface DeviceUpdate {
  label?: string;
  status?: DeviceStatus;
  /** Send null to clear a default; omit to leave unchanged. */
  default_department_id?: string | null;
  default_location_id?: string | null;
  /** Pair (id) or unpair (null) this device with an edge appliance. */
  appliance_id?: string | null;
}

export interface EnrollmentToken {
  /** Raw 8-character code, returned ONCE. Render as QR + text. */
  code: string;
  expires_at: string;
}

export async function listDevices(tenantId: string): Promise<Device[]> {
  const { data } = await apiClient.get<Device[]>(
    `/api/v1/tenants/${tenantId}/devices`,
  );
  return data;
}

/** One session's achieved rate for one sensor (a sparkline point). */
export interface DeviceSensorRatePoint {
  session_id: string;
  started_at: string;
  achieved_hz: number | null;
  /** ± rate jitter (std-dev of per-second Hz); null on older recordings. */
  std_hz: number | null;
  status: string | null;
}

/** A device's history for one logical sensor, oldest → newest. */
export interface DeviceSensorRateSeries {
  sensor_id: string;
  device_name: string | null;
  target_hz: number | null;
  low_count: number;
  total_count: number;
  points: DeviceSensorRatePoint[];
}

/** Per-device sensor rate history (ROADMAP CAPTURE-DEVICE-RATE-HISTORY). */
export interface DeviceRateHistory {
  device_id: string;
  session_count: number;
  sensors: DeviceSensorRateSeries[];
}

export async function getDeviceRateHistory(
  tenantId: string,
  deviceId: string,
): Promise<DeviceRateHistory> {
  const { data } = await apiClient.get<DeviceRateHistory>(
    `/api/v1/tenants/${tenantId}/devices/${deviceId}/rate-history`,
  );
  return data;
}

export async function issueEnrollmentToken(
  tenantId: string,
): Promise<EnrollmentToken> {
  const { data } = await apiClient.post<EnrollmentToken>(
    `/api/v1/tenants/${tenantId}/devices/enrollments`,
    {},
  );
  return data;
}

export async function updateDevice(
  tenantId: string,
  deviceId: string,
  body: DeviceUpdate,
): Promise<Device> {
  const { data } = await apiClient.patch<Device>(
    `/api/v1/tenants/${tenantId}/devices/${deviceId}`,
    body,
  );
  return data;
}

export async function retireDevice(
  tenantId: string,
  deviceId: string,
): Promise<void> {
  await apiClient.delete(`/api/v1/tenants/${tenantId}/devices/${deviceId}`);
}

// ---------------------------------------------------------------------
// Cross-tenant sharing (DEVICE-CROSS-TENANT-SHARING)
// ---------------------------------------------------------------------

/**
 * One row of a device's sharing matrix: a tenant the signed-in admin can
 * administer, and where this tablet stands in it.
 *
 * The list is bounded by the caller's own admin tenants — never the whole
 * platform — so it is short, and a tenant missing from it means "not yours to
 * share into", not "does not exist".
 */
export interface DeviceShare {
  tenant_id: string;
  tenant_slug: string;
  tenant_name: string;
  shared: boolean;
  /** The tenant being viewed from; it can't be un-shared here. */
  is_home: boolean;
  device_id: string | null;
  status: 'active' | 'suspended' | 'lost' | 'retired' | null;
  label: string | null;
}

export async function listDeviceShares(
  tenantId: string,
  deviceId: string,
): Promise<DeviceShare[]> {
  const { data } = await apiClient.get<DeviceShare[]>(
    `/api/v1/tenants/${tenantId}/devices/${deviceId}/shares`,
  );
  return data;
}

export async function shareDevice(
  tenantId: string,
  deviceId: string,
  targetTenantId: string,
): Promise<DeviceShare> {
  const { data } = await apiClient.put<DeviceShare>(
    `/api/v1/tenants/${tenantId}/devices/${deviceId}/shares/${targetTenantId}`,
  );
  return data;
}

export async function unshareDevice(
  tenantId: string,
  deviceId: string,
  targetTenantId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/v1/tenants/${tenantId}/devices/${deviceId}/shares/${targetTenantId}`,
  );
}
